import CDNImage from '@/components/ui/CDNImage';
import parse, {
  attributesToProps,
  DOMNode,
  domToReact,
  Element,
} from 'html-react-parser';
import { AnimatePresence, m, Variants } from 'motion/react';
import React, { useEffect, useState } from 'react';
import { lineTextVariants, viewPort } from 'utils/framer';
import CopyUrlButton from './copy-url';

const HEADING_TAGS = new Set(['h1', 'h2', 'h3']);
const VOID_ELEMENTS = new Set([
  'br',
  'hr',
  'input',
  'area',
  'base',
  'col',
  'embed',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);

interface CyclingNode {
  tag: string;
  content: React.ReactNode;
  props: Record<string, unknown>;
}

function CyclingText({ nodes }: { nodes: CyclingNode[] }) {
  const [index, setIndex] = useState(0);
  const { tag, props, content } = nodes[index];
  const MotionTag = (m as any)[tag];

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % nodes.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [nodes.length]);

  return (
    <div className="cycle-wrapper">
      <AnimatePresence mode="wait">
        <MotionTag
          key={index}
          {...props}
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ ease: [0.6, 0, 0.4, 1], duration: 0.5 }}
        >
          {content}
        </MotionTag>
      </AnimatePresence>
    </div>
  );
}

interface WysiwygProps {
  children: React.ReactNode;
  variants?: Variants;
  headingVariants?: Variants;
  customViewport?: { once?: boolean; margin?: string };
  stripAnchors?: boolean;
}

export default function Wysiwyg({
  children,
  variants,
  headingVariants,
  customViewport,
  stripAnchors,
}: WysiwygProps) {
  const html = typeof children === 'string' ? children : '';

  const options = {
    replace: (domNode: DOMNode) => {
      // 1. Check if node is an HTML tag (Element)
      if (domNode instanceof Element && domNode.name) {
        const TagName = domNode.name;

        // 1b. If void elements have no children/innerHTML - let the parser render them natively instead of wrapping in a motion component with a `children` prop
        if (VOID_ELEMENTS.has(TagName)) {
          return undefined;
        }

        // 1c. If stripAnchors is set, unwrap <a> tags (keep their content, drop the tag) -
        // needed when this content is rendered inside an outer <a>, since <a> can't nest in <a>
        if (stripAnchors && TagName === 'a') {
          return <>{domToReact(domNode.children as DOMNode[], options)}</>;
        }

        // 2. Check if this tag exists in 'm' (e.g. m.h1, m.div, m.p)
        // We cast 'm' to any to avoid strict type errors with dynamic keys
        const MotionComponent = (m as any)[TagName];

        // 3. Pick the right variants
        const activeVariants =
          HEADING_TAGS.has(TagName) && headingVariants
            ? headingVariants
            : variants;

        const isAnimatedTag = HEADING_TAGS.has(TagName) && !!headingVariants;

        // 4. Handle em with lineTextVariants
        if (
          TagName === 'p' &&
          domNode.attribs.class?.split(' ').includes('draw-line')
        ) {
          const props = attributesToProps(domNode.attribs);
          return (
            <m.p {...props}>
              <m.span
                variants={lineTextVariants}
                initial="hidden"
                whileInView="visible"
                viewport={customViewport ? customViewport : viewPort}
              />
              {domToReact(domNode.children as DOMNode[], options)}
            </m.p>
          );
        }

        // 5. Replace raw <img> tags with CDNImage (before the motion swap
        // below, since `m` provides an m.img that would otherwise wrap this
        // void element with children and crash)
        if (TagName === 'img') {
          const {
            src,
            alt,
            width,
            height,
            class: className,
            srcset,
            sizes,
            loading,
          } = domNode.attribs;

          return (
            <CDNImage
              src={src || ''}
              alt={alt || ''}
              width={Number(width) || 0}
              height={Number(height) || 0}
              className={className}
              srcSet={srcset}
              sizes={sizes}
              loading={loading === 'eager' ? 'eager' : 'lazy'}
            />
          );
        }

        // 6. Handle repeating tags cycling animation
        if (
          TagName === 'div' &&
          domNode.attribs.class?.split(' ').includes('cycle-wrapper')
        ) {
          const childElements = domNode.children.filter(
            (n) => n instanceof Element,
          ) as Element[];

          if (childElements.length > 1) {
            const cyclingNodes: CyclingNode[] = childElements.map((child) => ({
              tag: child.name,
              content: domToReact(child.children as DOMNode[], options),
              props: attributesToProps(child.attribs) as Record<
                string,
                unknown
              >,
            }));

            return <CyclingText nodes={cyclingNodes} />;
          }
        }

        // 7. If a Motion component exists and we have variants, swap the tag
        if (MotionComponent && activeVariants) {
          // Convert raw HTML attributes (class="foo") to React props (className="foo")
          const props = attributesToProps(domNode.attribs);

          return (
            <MotionComponent
              {...props}
              variants={activeVariants}
              initial="hidden"
              whileInView="visible"
              viewport={
                isAnimatedTag && customViewport ? customViewport : viewPort
              }
            >
              {domToReact(domNode.children as DOMNode[], options)}
            </MotionComponent>
          );
        }

        // 8. If a Copy URL link
        if (
          TagName === 'a' &&
          domNode.attribs.class?.split(' ').includes('copy-url')
        ) {
          const props = attributesToProps(domNode.attribs);

          return (
            <CopyUrlButton className={props.className as string | undefined}>
              {domToReact(domNode.children as DOMNode[], options)}
            </CopyUrlButton>
          );
        }
      }
    },
  };

  return <>{parse(html, options)}</>;
}
